import {
  XMarkIcon,
  ChevronDownIcon,
  PlusIcon,
} from '@heroicons/react/20/solid';
import React, { useCallback, useId, useMemo } from 'react';
import { createPortal } from 'react-dom';

import type { FormSelectizeProps, LoadingSpinnerProps } from './forms.types';
import type { SelectizeState } from './forms.types';
import type { SelectizeOption } from '@/types/forms';

import { useSelectize } from '@/hooks';

// =============================================================================
// LOADING SPINNER
// =============================================================================

function LoadingSpinner({ className = '' }: LoadingSpinnerProps) {
  return (
    <svg
      className={`animate-spin ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

function FormSelectizeInner<T extends SelectizeOption = SelectizeOption>(
  props: FormSelectizeProps<T>,
  _ref: React.ForwardedRef<HTMLInputElement>
): React.JSX.Element {
  const {
    label,
    error,
    success,
    helpText,
    required = false,
    size = 'md',
    disabled = false,
    placeholder = 'Select...',
    className = '',
    id,
    name,
    hideClear = false,
    emptyMessage = 'No options found',
    loadingMessage = 'Loading...',
    renderOption,
    renderTag,
    // Hook options
    options,
    loadOptions,
    value,
    onChange,
    multiple = false,
    creatable = false,
    createOption,
    debounceMs,
    minSearchLength,
    filterOption,
    closeOnSelect,
    maxSelections,
    dropdownBehavior,
  } = props;

  const reactId = useId();
  const inputId = id || `selectize-${reactId}`;

  const {
    isOpen,
    query,
    filteredOptions,
    selectedValues,
    highlightedIndex,
    setHighlightedIndex,
    isLoading,
    error: loadError,
    selectOption,
    removeOption,
    clearAll,
    handleKeyDown,
    handleInputChange,
    handleInputFocus,
    containerRef,
    inputRef,
    dropdownRef,
    canCreate,
    createFromQuery,
    dropdownPosition,
  } = useSelectize<T>({
    options,
    loadOptions,
    value,
    onChange,
    multiple,
    creatable,
    createOption,
    debounceMs,
    minSearchLength,
    disabled,
    filterOption,
    closeOnSelect,
    maxSelections,
    dropdownBehavior,
  });

  // Determine visual state
  const state: SelectizeState = error
    ? 'error'
    : success
      ? 'success'
      : 'default';

  // Group options by their group property
  const groupedOptions = useMemo(() => {
    const groups: { label: string | null; options: T[] }[] = [];
    const groupMap = new Map<string | null, T[]>();

    // Preserve order: first occurrence of each group defines its position
    for (const option of filteredOptions) {
      const groupKey = option.group ?? null;
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
        groups.push({ label: groupKey, options: groupMap.get(groupKey)! });
      }
      groupMap.get(groupKey)!.push(option);
    }

    return groups;
  }, [filteredOptions]);

  // Check if any options have groups (to conditionally render group headers)
  const hasGroups = useMemo(
    () => filteredOptions.some((opt) => opt.group),
    [filteredOptions]
  );

  // Size classes
  const sizeConfig = {
    sm: {
      container: 'min-h-9 py-1 px-2 text-body-sm',
      tag: 'text-body-sm px-1.5 py-0.5',
      tagIcon: 'h-3 w-3',
      input: 'text-body-lg md:text-body-sm',
      dropdown: 'text-body-sm',
      chevron: 'h-4 w-4',
    },
    md: {
      container: 'min-h-11 py-2 px-3 text-body-sm',
      tag: 'text-body-md px-2 py-0.5',
      tagIcon: 'h-3.5 w-3.5',
      input: 'text-body-lg md:text-body-sm',
      dropdown: 'text-body-sm',
      chevron: 'h-5 w-5',
    },
    lg: {
      container: 'min-h-[52px] py-2.5 px-4 text-body-md',
      tag: 'text-body-md px-2.5 py-1',
      tagIcon: 'h-4 w-4',
      input: 'text-body-lg md:text-body-md',
      dropdown: 'text-body-md',
      chevron: 'h-6 w-6',
    },
  };

  const sizes = sizeConfig[size];

  // State classes
  const stateClasses = {
    default: 'border-stroke-default focus-within:border-primary-400/50',
    error: 'border-error-500 focus-within:border-error-500',
    success: 'border-success-500 focus-within:border-success-500',
  };

  const labelStateClasses = {
    default: 'text-neutral-300',
    error: 'text-error-400',
    success: 'text-success-400',
  };

  // Container classes
  const containerClasses = [
    'relative flex flex-wrap items-center gap-1.5 w-full',
    'border rounded-lg bg-contrast-700 transition-all duration-200 focus-within:ring-state-focus-inset',
    sizes.container,
    stateClasses[state],
    disabled ? 'bg-contrast-700 opacity-60 cursor-not-allowed' : 'cursor-text',
    className,
  ].join(' ');

  // Handle container click to focus input
  const handleContainerClick = useCallback(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled, inputRef]);

  // Message to display
  const message = error || success || helpText || loadError;
  const messageId = `${inputId}-message`;

  // Default option renderer
  const defaultRenderOption = (option: T, _isHighlighted: boolean) => (
    <div className="flex items-center gap-2">
      {option.icon && (
        <span className="flex-shrink-0 text-neutral-400">{option.icon}</span>
      )}
      <div className="flex-1 min-w-0">
        <div className="truncate text-neutral-50">{option.label}</div>
        {option.subtitle && (
          <div className="truncate text-body-sm text-neutral-500">
            {option.subtitle}
          </div>
        )}
      </div>
      {option.disabled && (
        <span className="text-caption-sm text-neutral-600">Unavailable</span>
      )}
    </div>
  );

  // Default tag renderer
  const defaultRenderTag = (option: T, onRemove: () => void) => (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-md',
        'bg-sakura-wash text-primary-300 border border-stroke-accent',
        'transition-colors hover:bg-sakura-tint',
        sizes.tag,
      ].join(' ')}
    >
      {option.icon && (
        <span className="flex-shrink-0 opacity-70">{option.icon}</span>
      )}
      <span className="truncate max-w-[150px]">{option.label}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="ml-0.5 rounded-sm hover:bg-sakura-shade focus:outline-none focus-visible:ring-state-focus"
        aria-label={`Remove ${option.label}`}
      >
        <XMarkIcon className={sizes.tagIcon} />
      </button>
    </span>
  );

  return (
    <div className="space-y-1.5">
      {/* Label */}
      {label && (
        <label
          htmlFor={inputId}
          className={`block text-body-sm font-medium ${labelStateClasses[state]}`}
        >
          {label}
          {required && <span className="text-primary-400 ml-1">*</span>}
        </label>
      )}

      {/* Main container */}
      <div ref={containerRef} className="relative">
        <div
          className={containerClasses}
          onClick={handleContainerClick}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-owns={`${inputId}-listbox`}
          aria-controls={`${inputId}-listbox`}
        >
          {/* Selected values as tags (both single and multi mode) */}
          {selectedValues.map((option) => (
            <React.Fragment key={option.value}>
              {renderTag
                ? renderTag(option, () => removeOption(option))
                : defaultRenderTag(option, () => removeOption(option))}
            </React.Fragment>
          ))}

          {/* Input - always visible when no selection or when searching */}
          <input
            ref={inputRef}
            id={inputId}
            name={name}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={selectedValues.length > 0 ? '' : placeholder}
            className={[
              'flex-1 min-w-[60px] bg-transparent outline-none',
              'text-neutral-50 placeholder:text-neutral-500',
              sizes.input,
              // Hide input visually when single-select has value and not actively searching
              !multiple && selectedValues.length > 0 && !isOpen && !query
                ? 'w-0 min-w-0 opacity-0'
                : '',
            ].join(' ')}
            aria-autocomplete="list"
            aria-controls={`${inputId}-listbox`}
            aria-activedescendant={
              highlightedIndex >= 0
                ? `${inputId}-option-${highlightedIndex}`
                : undefined
            }
            aria-describedby={message ? messageId : undefined}
            aria-invalid={state === 'error'}
            autoComplete="off"
          />

          {/* Right side controls */}
          <div className="flex items-center gap-1 ml-auto">
            {/* Loading spinner */}
            {isLoading && (
              <LoadingSpinner className={`${sizes.chevron} text-primary-400`} />
            )}

            {/* Clear all button - only show in multi-select with 2+ items */}
            {!hideClear &&
              multiple &&
              selectedValues.length > 1 &&
              !disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                  className="p-0.5 rounded text-neutral-500 hover:text-neutral-300 hover:bg-contrast-600 transition-colors focus:outline-none focus-visible:ring-state-focus"
                  aria-label="Clear all selections"
                >
                  <XMarkIcon className={sizes.chevron} />
                </button>
              )}

            {/* Dropdown chevron */}
            <ChevronDownIcon
              className={[
                sizes.chevron,
                'text-neutral-500 transition-transform duration-200',
                isOpen ? 'rotate-180' : '',
              ].join(' ')}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      {/* Dropdown - portaled to body for proper stacking above modals */}
      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            id={`${inputId}-listbox`}
            role="listbox"
            aria-multiselectable={multiple}
            style={{
              position: 'fixed',
              top: dropdownPosition.top - window.scrollY,
              left: dropdownPosition.left - window.scrollX,
              width: dropdownPosition.width,
            }}
            className={[
              'z-[60] mt-1 pb-1 rounded-lg',
              'bg-contrast-700 border border-stroke-default',
              'shadow-lift-popover',
              'max-h-60 overflow-auto',
              'animate-in fade-in-0 zoom-in-95 duration-150',
              sizes.dropdown,
            ].join(' ')}
          >
            {/* Loading state */}
            {isLoading && (
              <div className="flex items-center gap-2 px-3 py-2 text-neutral-500">
                <LoadingSpinner className="h-4 w-4" />
                <span>{loadingMessage}</span>
              </div>
            )}

            {/* Error state */}
            {loadError && !isLoading && (
              <div className="px-3 py-2 text-error-400 text-body-md">
                {loadError}
              </div>
            )}

            {/* Options (grouped or flat) */}
            {!isLoading &&
              !loadError &&
              (() => {
                let optionIndex = 0;
                return groupedOptions.map((group) => (
                  <div key={group.label ?? '__ungrouped__'} role="group">
                    {/* Group header */}
                    {hasGroups && group.label && (
                      <div
                        className="px-3 py-1.5 text-label-sm font-semibold uppercase tracking-wider text-neutral-500 bg-contrast-700 sticky top-0"
                        role="presentation"
                      >
                        {group.label}
                      </div>
                    )}
                    {/* Group options */}
                    {group.options.map((option) => {
                      const currentIndex = optionIndex++;
                      return (
                        <div
                          key={option.value}
                          id={`${inputId}-option-${currentIndex}`}
                          role="option"
                          data-index={currentIndex}
                          aria-selected={selectedValues.some(
                            (sel) => sel.value === option.value
                          )}
                          aria-disabled={option.disabled}
                          onClick={() => {
                            if (!option.disabled) {
                              selectOption(option);
                            }
                          }}
                          onMouseEnter={() => setHighlightedIndex(currentIndex)}
                          className={[
                            'px-3 py-2 cursor-pointer transition-colors',
                            hasGroups && group.label ? 'pl-5' : '',
                            highlightedIndex === currentIndex
                              ? 'bg-contrast-600'
                              : 'hover:bg-contrast-600/50',
                            option.disabled
                              ? 'opacity-50 cursor-not-allowed'
                              : '',
                          ].join(' ')}
                        >
                          {renderOption
                            ? renderOption(
                                option,
                                highlightedIndex === currentIndex
                              )
                            : defaultRenderOption(
                                option,
                                highlightedIndex === currentIndex
                              )}
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}

            {/* Create option */}
            {canCreate && (
              <div
                role="option"
                onClick={createFromQuery}
                onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
                className={[
                  'px-3 py-2 cursor-pointer transition-colors',
                  'flex items-center gap-2 text-primary-400',
                  highlightedIndex === filteredOptions.length
                    ? 'bg-contrast-600'
                    : 'hover:bg-contrast-600/50',
                ].join(' ')}
              >
                <PlusIcon className="h-4 w-4" />
                <span>
                  Create &ldquo;<span className="font-medium">{query}</span>
                  &rdquo;
                </span>
              </div>
            )}

            {/* Empty state */}
            {!isLoading &&
              !loadError &&
              filteredOptions.length === 0 &&
              !canCreate && (
                <div className="px-3 py-2 text-neutral-500 text-center">
                  {emptyMessage}
                </div>
              )}
          </div>,
          document.body
        )}

      {/* Help/error/success message */}
      {message && (
        <p
          id={messageId}
          className={`text-label-xs ${
            error
              ? 'text-error-400'
              : success
                ? 'text-success-400'
                : 'text-neutral-500'
          }`}
        >
          {message}
        </p>
      )}
    </div>
  );
}

// Use type assertion for forwardRef with generics
const FormSelectize = React.forwardRef(FormSelectizeInner) as <
  T extends SelectizeOption = SelectizeOption,
>(
  props: FormSelectizeProps<T> & { ref?: React.ForwardedRef<HTMLInputElement> }
) => React.JSX.Element;

(FormSelectize as React.FC).displayName = 'FormSelectize';

export default FormSelectize;
